/**
 * emailReserved.js — RFC 6761 / 2606 reserved-domain check for email addresses.
 *
 * Centralized so every email-firing path (alertEmailDispatcher, emailService,
 * any new sender) shares the same suppression rule. Pre-existing was
 * scattered: alertEmailDispatcher had a local `isSuppressedRecipient` that
 * checked specific `@example.com/.org/.net` literals, missing TLD-level
 * coverage. emailService had no check at all.
 *
 * P22.2.1 carry-forward (Chapter 3 dogfood close 2026-05-01): the demo seed
 * (mig 295) uses `*@juuni-demo.example` addresses. The TLD `.example` is
 * RFC 6761 reserved for documentation; nothing delivered there can reach a
 * real inbox. The pre-fix domain-literal check missed this — in a production
 * env with real Resend keys, dashboard alerts + every transactional email
 * would have attempted delivery to fake addresses.
 *
 * Coverage:
 *   - `.example`, `.test`, `.invalid`, `.localhost` TLDs (any subdomain).
 *     RFC 6761 §6 special-use + RFC 2606 reserved.
 *   - `@test.local` literal — local-test convention.
 *   - `*.anon.local` — GDPR Article 17 anonymization marker (mig 258).
 *
 * Returns false on null/undefined/non-string input — defensive default; the
 * caller should treat that as "not reserved, but probably also not valid".
 */
export function isReservedEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const normalized = email.toLowerCase().trim();
  const atIdx = normalized.lastIndexOf('@');
  if (atIdx < 0) return false;
  const domain = normalized.slice(atIdx + 1);
  if (!domain) return false;

  // RFC 6761 §6 special-use + RFC 2606 reserved TLDs. Match `domain === tld`
  // to catch bare-TLD addresses (e.g. `user@localhost`) and
  // `domain.endsWith('.' + tld)` to catch any subdomain.
  const RESERVED_TLDS = ['example', 'test', 'invalid', 'localhost'];
  for (const tld of RESERVED_TLDS) {
    if (domain === tld || domain.endsWith('.' + tld)) return true;
  }

  // RFC 2606 §3 reserved second-level domains — distinct from the TLDs above
  // because they're regular .com / .org / .net domains (so the TLD-loop misses
  // them) but explicitly reserved for documentation. Match exact + any subdomain.
  const RESERVED_SLDS = ['example.com', 'example.org', 'example.net'];
  for (const sld of RESERVED_SLDS) {
    if (domain === sld || domain.endsWith('.' + sld)) return true;
  }

  // Project conventions
  return normalized.endsWith('.anon.local') ||  // GDPR Article 17 anonymization marker (per migration 258)
         normalized.endsWith('@test.local');
}
